import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { isNil, upperCase } from 'lodash-es';
import { useQueryClient } from '@tanstack/vue-query';

import { useInternational } from '@/common/locale';
import { useMessage } from '@/common/inject/useMessage';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import { useVueQueryErrorHandler } from '@/common/composables/useVueQueryErrorHandler';
import {
  getGetV8DashboardsQueryKey,
  getGetV8DashboardsFeaturedDashboardsQueryKey,
} from '@/api-client/metav8Over/queries/integrated-dashboard-v8-controller/integrated-dashboard-v8-controller';

import {
  DASHBOARD_NOT_PERMISSION_CODE_LIST,
  DASHBOARD_PERMISSION_CODE,
  DASHBOARD_TYPE,
} from '@/common/components/organisms/menuSearch/menuSearch.define';
import {
  DashboardType,
  MenuSearchFavoriteDashboardItem,
} from '@/common/components/organisms/menuSearch/menuSearch.type';
import { useFavoriteDashboards } from '@/common/components/organisms/menuSearch/menuSearch.uses';
import {
  useFavoriteDashboardsMutation,
  useMenuSearchPermission,
} from '@/common/components/organisms/menuSearch/menuSearch.service';

export const setup = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isShowMenuSearch } = storeToRefs(useBaseMenuStore());

  const { t } = useInternational();
  const { showErrorMsg } = useMessage();
  const { errorHandlerWithMessage } = useVueQueryErrorHandler();

  const { favoriteDashboardList } = useFavoriteDashboards();
  const { updateFavoriteDashboard } = useFavoriteDashboardsMutation();
  const { getDashboardPermission } = useMenuSearchPermission();

  const getDashboardType = (dashboardType: DashboardType) => {
    return t(DASHBOARD_TYPE[upperCase(dashboardType)]);
  };

  const handleClickFavorite = async ({
    dashboardId,
    favoriteId,
    dashboardType,
  }: {
    dashboardId: number;
    favoriteId: number;
    dashboardType: DashboardType;
  }) => {
    try {
      await updateFavoriteDashboard({ dashboardId, favoriteId });

      if (dashboardType === 'preset') {
        const found = queryClient.getQueryCache().find({
          queryKey: getGetV8DashboardsFeaturedDashboardsQueryKey(),
          exact: false,
        });
        if (found?.observers.length) await found.fetch();
      } else {
        const found = queryClient
          .getQueryCache()
          .find({ queryKey: getGetV8DashboardsQueryKey(), exact: true });
        if (found?.observers.length) await found.fetch();
      }
    } catch (error) {
      errorHandlerWithMessage({ error });
    }
  };

  const handleClick = async (item: MenuSearchFavoriteDashboardItem) => {
    const { dashboardId, path } = item;

    try {
      const permissionCode = await getDashboardPermission(dashboardId);

      if (isNil(permissionCode)) {
        return;
      }

      if (DASHBOARD_NOT_PERMISSION_CODE_LIST.includes(permissionCode)) {
        showErrorMsg({
          msgStr: t(DASHBOARD_PERMISSION_CODE[permissionCode]),
        });

        return;
      }

      router.push(path);
      isShowMenuSearch.value = false;
    } catch (error) {
      errorHandlerWithMessage({ error });
    }
  };

  return {
    favoriteDashboardList,

    getDashboardType,
    handleClick,
    handleClickFavorite,
  };
};
